import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useMemo, useRef } from "react";
import type { StylesConfig } from "react-select";
import { toast } from "react-toastify";
import { codeboxApi, CodeboxLockbox, BulkImportStatus } from "api/codebox";
import { lockboxAccountsApi } from "api/lockbox-accounts";
import { SquareLock02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { getErrorMessage } from "utils/error";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  CheckboxSize,
  InputSize,
  Modal,
  Select,
  Show
} from "shared/components";
import { Loader } from "shared/components/Loader";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";

const selectStyles: StylesConfig = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 })
};

interface ImportLockboxesModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const isTerminal = (s: BulkImportStatus) =>
  s.status === "done" || s.status === "failed" || s.status === "not_started";

export const ImportLockboxesModal = ({
  open,
  onClose,
  onSuccess
}: ImportLockboxesModalProps) => {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [accountId, setAccountId] = useState<number | null>(null);
  const seededRef = useRef(false);
  const pollingInitiatedRef = useRef(false);
  const [isPolling, setIsPolling] = useState(false);

  const { data: accounts = [], isLoading: isLoadingAccounts } = useQuery({
    queryKey: ["LOCKBOX_ACCOUNTS", "codebox"],
    queryFn: () => lockboxAccountsApi.list("codebox"),
    enabled: open
  });

  useEffect(() => {
    if (open && accountId === null && accounts.length === 1)
      setAccountId(accounts[0].id);
  }, [open, accountId, accounts]);

  const accountOptions = useMemo(
    () =>
      accounts.map((account) => ({ value: account.id, label: account.name })),
    [accounts]
  );

  const { data: lockboxes = [], isLoading } = useQuery({
    queryKey: ["CODEBOX_LOCKBOXES", accountId],
    queryFn: () => codeboxApi.getLockboxes(accountId!),
    enabled: open && accountId !== null,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  const importable = useMemo(
    () => lockboxes.filter((lb) => !lb.exists_in_boom),
    [lockboxes]
  );
  const existing = useMemo(
    () => lockboxes.filter((lb) => lb.exists_in_boom),
    [lockboxes]
  );

  useEffect(() => {
    if (!open) {
      seededRef.current = false;
      pollingInitiatedRef.current = false;
      setIsPolling(false);
      setAccountId(null);
      setSelected(new Set());
      return;
    }
    codeboxApi.getBulkImportStatus().then((s) => {
      if (s.status === "pending") {
        pollingInitiatedRef.current = true;
        setIsPolling(true);
      }
    });
  }, [open]);

  useEffect(() => {
    if (!open || isPolling) return;
    if (!seededRef.current && importable.length > 0) {
      seededRef.current = true;
      setSelected(new Set(importable.map((lb) => lb.serial_number)));
    }
  }, [open, isPolling, importable]);

  const { data: importStatus } = useQuery({
    queryKey: ["CODEBOX_BULK_IMPORT_STATUS"],
    queryFn: () => codeboxApi.getBulkImportStatus(),
    enabled: isPolling,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: (query) =>
      query.state.data && isTerminal(query.state.data) ? false : 1500
  });

  useEffect(() => {
    if (!importStatus || !isTerminal(importStatus)) return;
    if (!pollingInitiatedRef.current) return;
    pollingInitiatedRef.current = false;

    setIsPolling(false);
    if (importStatus.status === "not_started") return;

    queryClient.invalidateQueries({ queryKey: ["CODEBOX_LOCKBOXES"] });

    if (importStatus.status === "failed") {
      toast.error("Import failed");
      return;
    }

    const parts: string[] = [];
    if (importStatus.imported_count > 0)
      parts.push(
        `${importStatus.imported_count} lockbox${importStatus.imported_count !== 1 ? "es" : ""} imported`
      );
    if (importStatus.skipped_count > 0)
      parts.push(`${importStatus.skipped_count} already existed`);
    if (importStatus.failed_count > 0)
      parts.push(`${importStatus.failed_count} failed`);

    if (importStatus.failed_count > 0) toast.warn(parts.join(", "));
    else toast.success(parts.join(", ") || "Import complete");

    if (
      importStatus.failed_count === 0 &&
      (importStatus.imported_count > 0 || importStatus.skipped_count > 0)
    )
      onSuccess();
  }, [importStatus, queryClient, onSuccess]);

  const allImportableSelected =
    importable.length > 0 &&
    importable.every((lb) => selected.has(lb.serial_number));
  const someImportableSelected =
    importable.some((lb) => selected.has(lb.serial_number)) &&
    !allImportableSelected;

  const handleSelectAll = () => {
    if (allImportableSelected) setSelected(new Set());
    else setSelected(new Set(importable.map((lb) => lb.serial_number)));
  };

  const handleToggle = (lockbox: CodeboxLockbox) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(lockbox.serial_number)) next.delete(lockbox.serial_number);
      else next.add(lockbox.serial_number);
      return next;
    });
  };

  const { mutate: importLockboxes, isPending: isSubmitting } = useMutation({
    mutationKey: ["CODEBOX", "LOCKBOXES", "BULK_IMPORT"],
    mutationFn: () =>
      codeboxApi.bulkImportLockboxes(accountId!, Array.from(selected)),
    onSuccess: () => {
      pollingInitiatedRef.current = true;
      setIsPolling(true);
    },
    onError: (err) => toast.error(getErrorMessage(err))
  });

  const isPending = isSubmitting || isPolling;

  return (
    <Modal
      opened={open}
      onClose={onClose}
      panelClassName="w-full max-w-[480px] p-6 space-y-6"
    >
      <h2 className="text-xl font-bold text-content-1000">Import lockboxes</h2>

      <Show if={!isLoadingAccounts && accounts.length === 0}>
        <p className="text-sm text-content-400">
          No Codebox accounts connected. Add an account before importing
          lockboxes.
        </p>
      </Show>

      <Show if={accounts.length > 1}>
        <Select
          id="codebox-import-account"
          size={InputSize.SM}
          label="Codebox account"
          required
          searchable={false}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          styles={selectStyles}
          value={accountId ?? undefined}
          options={accountOptions}
          placeholder="Select an account"
          onChange={(e) => {
            const raw = e.target.value;
            setAccountId(raw ? Number(raw) : null);
            setSelected(new Set());
            seededRef.current = false;
          }}
        />
      </Show>

      <div className="max-h-[360px] overflow-y-auto space-y-4">
        <Show if={accounts.length > 1 && accountId === null}>
          <p className="text-sm text-content-400">
            Select an account to view its lockboxes.
          </p>
        </Show>

        <Show if={accountId !== null && isLoading}>
          <div className="flex items-center justify-center py-8">
            <Loader />
          </div>
        </Show>

        <Show if={accountId !== null && !isLoading && lockboxes.length === 0}>
          <div className="flex flex-col items-center justify-center gap-4 py-8">
            <div className="flex items-center justify-center size-16 rounded-full bg-alpha-40">
              <HugeiconsIcon
                icon={SquareLock02Icon}
                size={28}
                className="text-content-400"
              />
            </div>
            <div className="flex flex-col items-center gap-1 text-center">
              <p className="text-sm font-bold text-content-700">
                No lockboxes found
              </p>
              <p className="text-sm text-content-400">
                There are no lockboxes in your Codebox account.
              </p>
            </div>
          </div>
        </Show>

        <Show if={accountId !== null && !isLoading && lockboxes.length > 0}>
          <table className="w-full">
            <thead className="sticky top-0 bg-white">
              <tr className="h-12">
                <th className="w-14 align-middle text-left">
                  <Checkbox
                    size={CheckboxSize.SM}
                    checked={allImportableSelected}
                    indeterminate={someImportableSelected}
                    disabled={importable.length === 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th className="px-4 align-middle text-left text-xs font-normal text-content-400">
                  Name
                </th>
              </tr>
            </thead>
            <tbody>
              {importable.map((lb) => (
                <tr
                  key={lb.serial_number}
                  className="h-12 border-t border-alpha-40 hover:bg-background-900 cursor-pointer"
                  onClick={() => handleToggle(lb)}
                >
                  <td className="w-14 align-middle">
                    <Checkbox
                      size={CheckboxSize.SM}
                      checked={selected.has(lb.serial_number)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleToggle(lb);
                      }}
                    />
                  </td>
                  <td className="px-4 align-middle text-sm text-content-1000">
                    {lb.serial_number}
                  </td>
                </tr>
              ))}
              {existing.map((lb) => (
                <tr
                  key={lb.serial_number}
                  className="h-12 border-t border-alpha-40"
                >
                  <td className="w-14 align-middle">
                    <Checkbox
                      size={CheckboxSize.SM}
                      checked={false}
                      disabled
                      onChange={() => undefined}
                    />
                  </td>
                  <td className="px-4 align-middle text-sm text-content-200">
                    <Tooltip>
                      <TooltipTrigger className="text-left">
                        {lb.serial_number} has already been added
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[280px] rounded-lg z-[60] border bg-alpha-800 p-4 text-sm text-white">
                        <p>Code with this name already exists</p>
                      </TooltipContent>
                    </Tooltip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Show>
      </div>

      <div className="flex justify-end gap-3">
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.BASE}
          className="w-[125px]"
          onClick={onClose}
          disabled={isPending}
        >
          Skip
        </Button>
        <Button
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.BASE}
          className="w-[125px]"
          disabled={accountId === null || selected.size === 0 || isPending}
          loading={isPending}
          onClick={() => importLockboxes()}
        >
          Import
        </Button>
      </div>
    </Modal>
  );
};
